import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ICategory } from '../../../types/damTypes';
import { AiImagePublishSelection } from '../types';

interface PublishCategoryDialogProps {
  open: boolean;
  title: string;
  categories: ICategory[];
  submitting: boolean;
  onClose: () => void;
  onConfirm: (selection: AiImagePublishSelection) => Promise<void>;
}

function getParentCategoryId(category: ICategory): string | null {
  if (!category.parentCategory) return null;
  return typeof category.parentCategory === 'string'
    ? category.parentCategory
    : category.parentCategory._id;
}

const PublishCategoryDialog: React.FC<PublishCategoryDialogProps> = ({
  open,
  title,
  categories,
  submitting,
  onClose,
  onConfirm,
}) => {
  const [mainCategoryId, setMainCategoryId] = useState('');
  const [subCategoryId, setSubCategoryId] = useState('');
  const [assetName, setAssetName] = useState('');

  const mainCategories = useMemo(
    () => categories.filter((category) => !getParentCategoryId(category)),
    [categories]
  );

  const subCategories = useMemo(
    () => categories.filter((category) => getParentCategoryId(category) === mainCategoryId),
    [categories, mainCategoryId]
  );

  useEffect(() => {
    if (!open) return;
    setMainCategoryId('');
    setSubCategoryId('');
    setAssetName('');
  }, [open]);

  const handleConfirm = async () => {
    await onConfirm({
      mainCategoryId,
      subCategoryId: subCategoryId || undefined,
      assetName: assetName.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Välj vilken huvudkategori och eventuell underkategori bilden ska publiceras till i DAM.
          </Typography>

          <TextField
            fullWidth
            size="small"
            label="Vad vill du döpa bilden till?"
            value={assetName}
            onChange={(event) => setAssetName(event.target.value)}
            placeholder="Ange namn för titel och filnamn i DAM"
            helperText="Används som både titel och filnamn i DAM. Lämna tomt för automatiskt namn."
          />

          <FormControl fullWidth size="small">
            <InputLabel id="ai-image-main-category-label">Huvudkategori</InputLabel>
            <Select
              labelId="ai-image-main-category-label"
              value={mainCategoryId}
              label="Huvudkategori"
              onChange={(event) => {
                setMainCategoryId(String(event.target.value));
                setSubCategoryId('');
              }}
            >
              {mainCategories.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={!mainCategoryId || subCategories.length === 0}>
            <InputLabel id="ai-image-sub-category-label">Underkategori</InputLabel>
            <Select
              labelId="ai-image-sub-category-label"
              value={subCategoryId}
              label="Underkategori"
              onChange={(event) => setSubCategoryId(String(event.target.value))}
            >
              <MenuItem value="">Ingen underkategori</MenuItem>
              {subCategories.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {mainCategoryId && subCategories.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              Den valda huvudkategorin har inga underkategorier.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Avbryt
        </Button>
        <Button variant="contained" onClick={() => void handleConfirm()} disabled={!mainCategoryId || submitting}>
          {submitting ? 'Sparar...' : 'Fortsätt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishCategoryDialog;
